import { useState, useEffect, useCallback } from "react";
import AdminLayout from "@/pages/admin/components/AdminLayout";
import { supabase } from "@/lib/supabase";
import RichTextEditor from "@/components/base/RichTextEditor";
import ImageUploader from "@/components/base/ImageUploader";

const categories = ["All", "Health", "Education", "Protection", "Economic Security", "General"];
const categoryColors: Record<string, string> = {
  Health: "bg-emerald-100 text-emerald-700",
  Education: "bg-amber-100 text-amber-700",
  Protection: "bg-rose-100 text-rose-700",
  "Economic Security": "bg-teal-100 text-teal-700",
  General: "bg-stone-100 text-stone-600",
};

const dbCategories = ["Health", "Education", "Protection", "Economic Security", "General"];

type FormData = {
  title: string;
  author: string;
  category: string;
  status: "Published" | "Draft";
  readTime: string;
  excerpt: string;
  imageUrl: string;
  content: string;
  date: string;
};

const emptyForm: FormData = {
  title: "",
  author: "",
  category: "Health",
  status: "Draft",
  readTime: "",
  excerpt: "",
  imageUrl: "",
  content: "",
  date: new Date().toISOString().split("T")[0],
};

export default function AdminArticlesPage() {
  const [articles, setArticles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormData>(emptyForm);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [charCount, setCharCount] = useState(0);
  const [previewHtml, setPreviewHtml] = useState("");

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("articles")
      .select("*")
      .order("date", { ascending: false });
    if (error) {
      console.error("Error fetching articles:", error);
    } else {
      setArticles(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const filtered = articles.filter((a) => {
    const matchCat = filterCat === "All" || a.category === filterCat;
    const matchStatus = filterStatus === "All" || a.status === filterStatus;
    const matchSearch =
      a.title.toLowerCase().includes(search.toLowerCase()) ||
      a.author.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchStatus && matchSearch;
  });

  const openAdd = () => {
    setEditingId(null);
    setForm({
      ...emptyForm,
      date: new Date().toISOString().split("T")[0],
    });
    setCharCount(0);
    setPreviewHtml("");
    setShowModal(true);
  };

  const openEdit = (a: any) => {
    setEditingId(a.id);
    setForm({
      title: a.title,
      author: a.author,
      category: a.category,
      status: a.status,
      readTime: a.read_time || "",
      excerpt: a.excerpt || "",
      imageUrl: a.image_url || "",
      content: a.content || "",
      date: a.date ? new Date(a.date).toISOString().split("T")[0] : "",
    });
    setCharCount((a.excerpt || "").length);
    setPreviewHtml(a.content || "");
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.author.trim()) return;
    if (editingId !== null) {
      const { error } = await supabase
        .from("articles")
        .update({
          title: form.title,
          author: form.author,
          category: form.category,
          status: form.status,
          read_time: form.readTime,
          excerpt: form.excerpt,
          image_url: form.imageUrl,
          content: form.content,
          date: form.date,
          updated_at: new Date().toISOString(),
        })
        .eq("id", editingId);
      if (error) {
        console.error("Error updating article:", error);
        return;
      }
    } else {
      const { error } = await supabase.from("articles").insert({
        title: form.title,
        author: form.author,
        category: form.category,
        status: form.status,
        read_time: form.readTime,
        excerpt: form.excerpt,
        image_url: form.imageUrl,
        content: form.content,
        date: new Date().toISOString(),
      });
      if (error) {
        console.error("Error creating article:", error);
        return;
      }
    }
    setShowModal(false);
    fetchArticles();
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("articles").delete().eq("id", id);
    if (error) {
      console.error("Error deleting article:", error);
      return;
    }
    setDeleteId(null);
    fetchArticles();
  };

  const toggleStatus = async (id: string, current: string) => {
    const next = current === "Published" ? "Draft" : "Published";
    const { error } = await supabase.from("articles").update({ status: next }).eq("id", id);
    if (error) {
      console.error("Error toggling status:", error);
      return;
    }
    fetchArticles();
  };

  const formatDate = (d: string) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <AdminLayout title="Articles" subtitle="Manage all news articles published on the website">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
        <div className="relative flex-1 w-full sm:max-w-xs">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
            <i className="ri-search-line text-stone-400 text-sm"></i>
          </div>
          <input
            type="text"
            placeholder="Search articles..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-stone-200 bg-white text-sm focus:outline-none focus:border-primary text-stone-700 placeholder-stone-400"
          />
        </div>
        <select
          value={filterCat}
          onChange={(e) => setFilterCat(e.target.value)}
          className="px-3 py-2.5 rounded-xl border border-stone-200 bg-white text-sm text-stone-600 focus:outline-none focus:border-primary cursor-pointer"
        >
          {categories.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2.5 rounded-xl border border-stone-200 bg-white text-sm text-stone-600 focus:outline-none focus:border-primary cursor-pointer"
        >
          <option>All</option>
          <option>Published</option>
          <option>Draft</option>
        </select>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-primary-dark transition-colors duration-200 whitespace-nowrap cursor-pointer ml-auto"
        >
          <i className="ri-add-line"></i>
          New Article
        </button>
      </div>

      {/* Summary */}
      <div className="flex items-center gap-4 mb-4">
        <p className="text-sm text-stone-500">
          {filtered.length} article{filtered.length !== 1 ? "s" : ""} found
        </p>
        <span className="text-stone-300">|</span>
        <p className="text-sm text-stone-500">
          {articles.filter((a) => a.status === "Published").length} published &middot;{" "}
          {articles.filter((a) => a.status === "Draft").length} drafts
        </p>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <i className="ri-loader-4-line animate-spin text-primary text-2xl"></i>
        </div>
      )}

      {/* Table */}
      {!loading && (
        <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-stone-100 bg-stone-50">
                  <th className="text-left px-6 py-3.5 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                    Article
                  </th>
                  <th className="text-left px-4 py-3.5 text-xs font-semibold text-stone-500 uppercase tracking-wider hidden md:table-cell">
                    Category
                  </th>
                  <th className="text-left px-4 py-3.5 text-xs font-semibold text-stone-500 uppercase tracking-wider hidden lg:table-cell">
                    Author
                  </th>
                  <th className="text-left px-4 py-3.5 text-xs font-semibold text-stone-500 uppercase tracking-wider hidden lg:table-cell">
                    Date
                  </th>
                  <th className="text-left px-4 py-3.5 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-right px-6 py-3.5 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-50">
                {filtered.map((a) => (
                  <tr key={a.id} className="hover:bg-stone-50/50 transition-colors duration-150">
                    <td className="px-6 py-4">
                      <p className="text-sm font-semibold text-stone-800 line-clamp-1">{a.title}</p>
                      <p className="text-xs text-stone-400 mt-0.5 line-clamp-1 hidden sm:block">{a.excerpt}</p>
                    </td>
                    <td className="px-4 py-4 hidden md:table-cell">
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold ${categoryColors[a.category] ?? "bg-stone-100 text-stone-500"}`}
                      >
                        {a.category}
                      </span>
                    </td>
                    <td className="px-4 py-4 hidden lg:table-cell">
                      <span className="text-sm text-stone-600">{a.author}</span>
                    </td>
                    <td className="px-4 py-4 hidden lg:table-cell">
                      <span className="text-sm text-stone-400">{formatDate(a.date)}</span>
                    </td>
                    <td className="px-4 py-4">
                      <button
                        onClick={() => toggleStatus(a.id, a.status)}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold cursor-pointer transition-all duration-200 ${
                          a.status === "Published"
                            ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                            : "bg-stone-100 text-stone-500 hover:bg-stone-200"
                        }`}
                      >
                        {a.status}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEdit(a)}
                          className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-primary hover:bg-primary/10 rounded-lg cursor-pointer transition-all duration-200"
                        >
                          <i className="ri-edit-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => setDeleteId(a.id)}
                          className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-lg cursor-pointer transition-all duration-200"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="text-center py-16">
                <div className="w-12 h-12 flex items-center justify-center bg-stone-100 rounded-full mx-auto mb-3">
                  <i className="ri-article-line text-stone-400 text-xl"></i>
                </div>
                <p className="text-stone-500 text-sm font-medium">No articles found</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-5 border-b border-stone-100">
              <h2 className="text-base font-bold text-stone-800">{editingId ? "Edit Article" : "New Article"}</h2>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-600 cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="px-6 py-5 flex flex-col gap-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Title *</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="Article title"
                    className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-800 placeholder-stone-400"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Author *</label>
                  <input
                    type="text"
                    value={form.author}
                    onChange={(e) => setForm({ ...form, author: e.target.value })}
                    placeholder="Author name"
                    className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-800 placeholder-stone-400"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Read Time</label>
                  <input
                    type="text"
                    value={form.readTime}
                    onChange={(e) => setForm({ ...form, readTime: e.target.value })}
                    placeholder="e.g. 5 min"
                    className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-800 placeholder-stone-400"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value as "Published" | "Draft" })}
                    className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-700 cursor-pointer"
                  >
                    <option>Draft</option>
                    <option>Published</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Category</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-700 cursor-pointer"
                  >
                    {dbCategories.map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Publish Date</label>
                  <input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-800 cursor-pointer"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Cover Image</label>
                <ImageUploader
                  value={form.imageUrl}
                  onChange={(url) => setForm({ ...form, imageUrl: url })}
                  placeholder="Paste URL or upload an image"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Excerpt</label>
                <textarea
                  value={form.excerpt}
                  onChange={(e) => {
                    setForm({ ...form, excerpt: e.target.value });
                    setCharCount(e.target.value.length);
                  }}
                  rows={2}
                  maxLength={500}
                  placeholder="Brief description for listing cards..."
                  className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-800 placeholder-stone-400 resize-none"
                />
                <p className="text-xs text-stone-400 text-right">{charCount}/500</p>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Full Content</label>
                <RichTextEditor
                  value={form.content}
                  onChange={(html) => {
                    setForm({ ...form, content: html });
                    setPreviewHtml(html);
                  }}
                  placeholder="Write the full article content here..."
                />
              </div>
              {form.content && (
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Preview</label>
                  <div
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-sm text-stone-800 overflow-auto max-h-40 prose prose-sm"
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                  />
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-stone-100">
              <button
                onClick={() => setShowModal(false)}
                className="px-5 py-2.5 rounded-xl text-sm font-semibold text-stone-600 hover:bg-stone-100 cursor-pointer transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!form.title.trim() || !form.author.trim()}
                className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-primary text-white hover:bg-primary-dark cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingId ? "Save Changes" : "Create Article"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6">
            <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-full mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-500 text-xl"></i>
            </div>
            <h3 className="text-base font-bold text-stone-800 text-center mb-2">Delete Article?</h3>
            <p className="text-sm text-stone-500 text-center mb-6">
              This action cannot be undone. The article will be permanently removed.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteId(null)}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-stone-600 border border-stone-200 hover:bg-stone-50 cursor-pointer transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteId)}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold bg-red-500 text-white hover:bg-red-600 cursor-pointer transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}